import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { GroupedVirtuoso } from 'react-virtuoso';
import { useShallow } from 'zustand/react/shallow';
import { DataQueryKey } from '../../../../../hooks/useDataQuery/dataQuery.types';
import { useStack } from '../../../../../hooks/useStack';
import LangEnum from '../../../../../lang/LangEnum';
import { patchCatalogKitOrderQuantity } from '../../../../../services/modules/catalogProducts/catalogKitQuantityCache.utils';
import useOrderBookStore from '../../../../../stores/OrderBook/orderBook.store';
import { useLoadingStatusStore } from '../../../../context/loadingStatus/LoadingStatus.store';
import { ordersRepository } from '../../../../orders/repositories/ordersRepository/orders.repository';
import { useTranslation } from '../../../../translation/TranslationContext';
import { KitCombinationTypes } from '../../types/kit-combinations.types';
import KitDetailsCombination from './components/KitDetailsCombination/KitDetailsCombination';
import kitDetailsListComponents from './components/KitDetailsListComponents';
import KitDetailsSectionHeader from './components/KitDetailsSectionHeader/KitDetailsSectionHeader';
import KitItemCombinationListItem from './components/KitItemCombination/KitItemCombination';
import useHandleUpdateKitOnOrderErrorResponse from './hooks/useHandleUpdateKitErrorResponse';
import useKitDetailsList from './hooks/useKitDetailsList';
import { KitDetailsListProps, StackKit } from './types/kitDetailsList.types';

const KitDetailsList = ({ scrollParent, disableGrid, isOfflineMode }: KitDetailsListProps) => {
  const getUniqueKey = (item: StackKit) => `${item.kitId}-${item.orderId}`;
  const { translate } = useTranslation();
  const { handleError } = useHandleUpdateKitOnOrderErrorResponse();
  const handleLoadingStatus = useLoadingStatusStore((state) => state.handleLoadingStatus);
  const queryClient = useQueryClient();
  const { selectedOrderId, reloadOrderItemsQuantity } = useOrderBookStore(
    useShallow((state) => ({
      reloadOrderItemsQuantity: state.reloadOrderItemsQuantity,
      selectedOrderId: state.selectedOrder?.id,
    })),
  );
  const [errorMap, setErrorMap] = useState<Record<string, number>>({});

  const {
    groups,
    isLoading,
    groupCounts,
    combinations,
    kitCombinationsCount,
    handleNextPage,
    refetchGetKitCombinations,
    refetchGetKitCombinationsCount,
  } = useKitDetailsList();

  const refetchKitRelations = useCallback(
    (kitStack: StackKit[]) => {
      queryClient.refetchQueries({
        predicate: (query) => {
          const isIncludedOrderId = kitStack.some((kit) => {
            const isKitCombinationQuery =
              query.queryKey.includes(kit.kitId) &&
              query.queryKey.includes(DataQueryKey.KIT_COMBINATIONS);
            const isKitCombinationsCountQuery =
              query.queryKey.includes(kit.kitId) &&
              query.queryKey.includes(DataQueryKey.KIT_COMBINATIONS_COUNT);

            if (!isKitCombinationQuery && !isKitCombinationsCountQuery) {
              return false;
            }

            return query.queryKey.includes(kit.orderId);
          });

          return isIncludedOrderId;
        },
      });
    },
    [refetchGetKitCombinations, refetchGetKitCombinationsCount],
  );

  const handleCreateOrUpdateKitOnOrder = async (kitStack: StackKit[]) => {
    for await (const kit of kitStack) {
      const repository = ordersRepository(kit.orderId);

      await handleLoadingStatus({
        requestFn: () => repository.createOrUpdateOrderListWithKit(kit),
        onFailure: (error) => {
          handleError(error);
          setErrorMap((prev) => {
            const key = `${kit.kitId}-${kit.orderId}`;

            return {
              ...prev,
              [key]: (prev[key] || 0) + 1,
            };
          });
        },
        onSuccess: async () => {
          const isSelectedOrder = kit.orderId === selectedOrderId;

          patchCatalogKitOrderQuantity({
            queryClient,
            orderId: kit.orderId,
            kitId: kit.kitId,
            orderQuantity: kit.quantity,
          });

          await reloadOrderItemsQuantity(true, kit.orderId, isSelectedOrder);
        },
      });
    }

    refetchKitRelations(kitStack);
  };

  const { pushItem } = useStack<StackKit>({
    getUniqueKey,
    onFinish: handleCreateOrUpdateKitOnOrder,
  });

  if (isLoading) {
    return null;
  }

  if (!groups.length) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {translate(LangEnum.PRODUCT_DETAILS_UNAVAILABLE_GRID)}
        </Typography>
      </Box>
    );
  }

  return (
    <GroupedVirtuoso
      style={{
        width: '100%',
        height: '100%',
      }}
      customScrollParent={scrollParent ?? undefined}
      tabIndex={-1}
      groupCounts={groupCounts}
      components={kitDetailsListComponents}
      endReached={() => {
        if (!combinations.length) {
          return;
        }

        handleNextPage();
      }}
      groupContent={(groupIndex) => {
        const group = groups[groupIndex];

        if (!group) {
          return null;
        }

        const countFromCombination = kitCombinationsCount?.find(
          (item) =>
            group.combination.type === KitCombinationTypes.CLOSED_KIT &&
            item.orderId === group.combination.orderId,
        );

        return (
          <KitDetailsSectionHeader combination={group.combination} count={countFromCombination} />
        );
      }}
      itemContent={(index) => {
        const combination = combinations[index];

        if (!combination) {
          return null;
        }

        if (combination.type !== KitCombinationTypes.CLOSED_KIT) {
          return <KitItemCombinationListItem combination={combination} />;
        }

        return (
          <KitDetailsCombination
            key={`${combination.kitId}-${combination.orderId}`}
            pushItem={pushItem}
            isOfflineMode={isOfflineMode}
            errorVersion={errorMap[`${combination.kitId}-${combination.orderId}`] ?? 0}
            disableGrid={disableGrid}
            combination={combination}
            refetchKitRelations={refetchKitRelations}
          />
        );
      }}
    />
  );
};

export default KitDetailsList;
